import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { fmtMoney, fmtErr, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { CreditCard, Bank, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PayInvoice() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [etransfer, setEtransfer] = useState(null);
  const [checking, setChecking] = useState(false);
  const polled = useRef(false);

  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
    const sid = params.get("session_id");
    if (sid && !polled.current) {
      polled.current = true;
      setChecking(true);
      const t = setInterval(async () => {
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid") {
            clearInterval(t);
            setChecking(false);
            toast.success("Payment received — thank you!");
            setParams({});
            load();
          }
        } catch { /* payment poll retry */ }
      }, 2000);
      setTimeout(() => { clearInterval(t); setChecking(false); }, 30000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load + payment poll by design
  }, [id]);

  if (!inv) return <Layout title="Pay invoice"><p className="text-muted-foreground">Loading…</p></Layout>;

  const payCard = async () => {
    try {
      const r = await api.post("/payments/checkout", { invoice_id: id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const showEtransfer = async () => {
    try { const r = await api.post(`/invoices/${id}/etransfer`); setEtransfer(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (inv.status === "paid") {
    return (
      <Layout title={inv.number}>
        <div className="card-flat space-y-3 p-8 text-center" data-testid="payment-success">
          <CheckCircle size={56} weight="bold" className="mx-auto text-primary" />
          <p className="font-heading text-2xl font-bold">Paid — thank you</p>
          <p className="text-sm text-muted-foreground">{fmtMoney(inv.total)} via {inv.payment_method === "card" ? "card" : "e-Transfer"}</p>
        </div>
      </Layout>
    );
  }

  if (inv.status === "cancelled") {
    return (
      <Layout title={inv.number}>
        <div className="card-flat space-y-2 p-8 text-center" data-testid="invoice-cancelled">
          <p className="font-heading text-xl font-bold">This invoice was cancelled</p>
          <p className="text-sm text-muted-foreground">{inv.cancel_reason || "Nothing is owed."}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={inv.number}>
      <div className="space-y-6">
        {checking && <div className="card-flat p-4 text-center text-sm font-semibold" data-testid="checking-payment">Confirming your card payment…</div>}
        <div className="card-flat p-6" data-testid="invoice-summary">
          {inv.business_name && <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground" data-testid="invoice-business">{inv.business_name}</p>}
          <p className="font-heading text-xl font-bold">{inv.property_name}</p>
          {inv.billing?.name && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="invoice-bill-to">
              Bill to: {inv.billing.name}{inv.billing.company ? ` — ${inv.billing.company}` : ""}{inv.billing.address ? ` · ${inv.billing.address}` : ""}
            </p>
          )}
          <div className="mt-4 space-y-2">
            {inv.line_items.map((l, i) => (
              <div key={`${l.kind}-${l.description}`} className="flex justify-between text-sm">
                <span>{l.description}</span><span className="font-semibold">{fmtMoney(l.amount)}</span>
              </div>
            ))}
          </div>
          {inv.line_items.some((l) => l.receipt) && (
            <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid="receipt-photos">
              <p className="label-caps">Receipts</p>
              <div className="flex flex-wrap gap-2">
                {inv.line_items.filter((l) => l.receipt).map((l, i) => (
                  <a key={i} href={photoSrc(l.receipt)} target="_blank" rel="noopener noreferrer" data-testid={`receipt-${i}`}>
                    <img src={photoSrc(l.receipt)} alt={l.description} className="h-20 w-20 rounded-md object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 space-y-1 border-t border-border pt-4">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(inv.subtotal ?? inv.total)}</span></div>
            {!!inv.credits_total && <div className="flex justify-between text-sm" data-testid="credit-line"><span className="text-muted-foreground">Credits</span><span>{fmtMoney(inv.credits_total)}</span></div>}
            {!!inv.credits_total && <div className="flex justify-between text-sm" data-testid="taxable-base"><span className="text-muted-foreground">Taxable amount</span><span>{fmtMoney(inv.taxable_base ?? (inv.subtotal + inv.credits_total))}</span></div>}
            {inv.tax_amount > 0 && <div className="flex justify-between text-sm" data-testid="tax-line"><span className="text-muted-foreground">{inv.tax_label_used || "HST"} ({inv.tax_rate_used ?? inv.hst_rate_used}%)</span><span>{fmtMoney(inv.tax_amount)}</span></div>}
            <div className="flex justify-between pt-1">
              <span className="label-caps">Total due</span>
              <span className="font-heading text-3xl font-bold" data-testid="amount-due">{fmtMoney(inv.total)}</span>
            </div>
            {(inv.tax_number_used || inv.hst_number) && <p className="text-xs text-muted-foreground">{inv.tax_label_used || "HST"} # {inv.tax_number_used ?? inv.hst_number}</p>}
          </div>
        </div>

        <section className="space-y-3" data-testid="payment-options">
          <p className="label-caps">How do you want to pay?</p>
          <button data-testid="pay-card-btn" onClick={payCard} disabled={inv.card_available === false}
            className="card-flat flex w-full items-center gap-4 p-5 text-left transition-colors hover:bg-muted/50 disabled:opacity-50">
            <CreditCard size={32} weight="bold" className="text-primary" />
            <div className="flex-1">
              <p className="font-bold">Credit / debit card</p>
              <p className="text-sm text-muted-foreground">
                {inv.card_available === false ? "Not available — this business hasn't set up card payments" : `Instant · ~${fmtMoney(inv.card_fee)} card processing cost`}
              </p>
            </div>
            <span className="font-heading text-lg font-bold">{fmtMoney(inv.total)}</span>
          </button>
          {inv.etransfer_available !== false && (
            <button data-testid="pay-etransfer-btn" onClick={showEtransfer} className="card-flat flex w-full items-center gap-4 p-5 text-left transition-colors hover:bg-muted/50">
              <Bank size={32} weight="bold" className="text-primary" />
              <div className="flex-1">
                <p className="font-bold">Interac e-Transfer</p>
                <p className="text-sm text-muted-foreground">No fees · confirmed manually (1 business day)</p>
              </div>
              <span className="font-heading text-lg font-bold">{fmtMoney(inv.total)}</span>
            </button>
          )}
          <p className="text-xs text-muted-foreground" data-testid="fee-note">
            {inv.card_available === false
              ? "Card payment isn't available for this business yet — e-Transfer below is free."
              : `Card payment costs about ${fmtMoney(inv.card_fee)} in processing; e-Transfer is free but slower.`}
          </p>
        </section>

        {etransfer && (
          <div className="card-flat space-y-2 border-primary p-5" data-testid="etransfer-instructions">
            <p className="font-heading text-lg font-bold">e-Transfer instructions</p>
            <p className="text-sm">Send <span className="font-bold">{fmtMoney(etransfer.amount)}</span> to:</p>
            <p className="rounded-md bg-muted p-3 font-mono text-sm font-bold" data-testid="etransfer-email">{etransfer.email}</p>
            <p className="text-sm">Reference / message: <span className="font-bold" data-testid="etransfer-ref">{etransfer.reference}</span></p>
            <p className="text-xs text-muted-foreground">{etransfer.note}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
